import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

// min-h/w-touch on every variant: controls must clear 44px whether they render as text or
// icon-only. The `compact` size is the deliberate exception — see below.
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 disabled:pointer-events-none disabled:opacity-50 min-h-touch',
  {
    variants: {
      variant: {
        default: 'bg-stone-900 text-white hover:bg-stone-700 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-300',
        outline:
          'border border-stone-300 bg-transparent hover:bg-stone-100 dark:border-stone-700 dark:hover:bg-stone-800',
        ghost: 'hover:bg-stone-100 dark:hover:bg-stone-800',
        destructive: 'bg-red-600 text-white hover:bg-red-500',
        link: 'text-stone-900 underline-offset-4 hover:underline dark:text-stone-100 min-h-0',
      },
      size: {
        default: 'px-4 py-2',
        sm: 'px-3 py-1.5 text-sm',
        icon: 'min-w-touch p-0',
        // One caller, and one deliberate exception to the 44px rule: the `×` inside a 24px
        // tag chip, which a full-size box bursts open. It sits beside a full-size target
        // and destroys nothing but one chip on a form still being typed. (The ingredient
        // row's delete was the second until M3.6 moved it into the row's popover, where
        // there is room for it at full size.) Everything else stays ≥44px. The utilities
        // win over the global `@layer base` rule for the same reason `link`'s `min-h-0` does.
        compact: 'h-7 w-7 min-h-0 min-w-0 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    )
  },
)
Button.displayName = 'Button'
